<template>
  <div>
    <transition name="fast-fade">
      <div v-if="source" class="background">
        <transition name="fade-in" mode="in-out">
          <div class="background__wrapper" :key="activeSource + sourceType">
            <img
              v-if="sourceType === 'image'"
              :src="activeSource"
            >
            <iframe
              v-if="sourceType === 'iframe'"
              class="background__iframe"
              :src="activeSource"
              :style="iframeStyle"
              frameborder="0"
              allowfullscreen
              sandbox="allow-scripts allow-forms allow-same-origin"
              @load="handleIframeLoad"
              @error="handleIframeError"
            />

            <transition name="fast-fade">
              <div
                v-if="sourceType === 'iframe' && isIframeLoading"
                class="iframe-loader"
              >
                <div class="iframe-loader__spinner"></div>
                <div class="iframe-loader__text">Loading…</div>
              </div>
            </transition>

            <video
              v-if="sourceType === 'video'"
              :src="activeSource"
              ref="video"
              :muted="isVideoMuted"
              :autoplay="!isTransitionVideo"
              playsinline
              disablePictureInPicture
              :loop="shouldLoop"
              poster="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
              @ended="handleVideoEnd"
            />
            <div
              v-if="sourceType === 'video' && ( backgroundVideoSettings ? backgroundVideoSettings.showControls : true)"
              class="background__control"
              :style="{ bottom: controlsBottomPx, right: controlsRightPx }"
            >
              <div class="control control--mute touch" @click="toggleMute">
                <fa v-if="isVideoMuted" :icon="['fal', 'volume-mute']"/>
                <fa v-if="!isVideoMuted" :icon="['fal', 'volume']"/>
              </div>
              <div class="control control--playback touch" @click="togglePlayback">
                <fa v-if="isVideoPlaying" :icon="['fal', 'pause']"/>
                <fa v-if="!isVideoPlaying" :icon="['fal', 'play']"/>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { bus } from '../main'
import _ from 'lodash'
import { isLivingEntryRoute, isLivingOptionRoute } from '@/utils/livingRoute'

export default {
  props: ['source'],

  data () {
    return {
      activeSource: '',
      isTransitionVideo: false,
      isVideoPlaying: true,
      isVideoMuted: false,
      transitionName: 'fade-in',
      controlsBottom: 0, // px number
      controlsRight: 0, // px number
      iframeBottomOffset: 0, // px

      controlsRafId: null,
      iframeRafId: null,

      isIframeLoading: false,
      iframeLoadTimeout: null,
    }
  },

  computed: {
    ...mapGetters({
      config: 'config',
      activeLivingOption: 'property/activeLivingOption'
    }),

    shouldLoop() {
      if (this.isTransitionVideo) return false
      return this.backgroundVideoSettings ? this.backgroundVideoSettings.loop : false
    },

    sourceType () {
      if (this.activeSource) {
        if (this.isIframePage) return 'iframe'

        const ext = this.activeSource.split('.').pop().toLowerCase()
        if (ext === 'mov' || ext === 'mp4') {
          return 'video'
        }

        if (ext === 'jpeg' || ext === 'jpg' || ext === 'png' || ext === 'gif') {
          return 'image'
        }

        return 'unsupported'
      }
      return ''
    },

    backgroundVideoSettings () {
      if (this.$store.getters.activePage) {
        return this.$store.getters.activePage.backgroundVideoSettings
      } else if (isLivingEntryRoute(this.$route.name)) {
        return this.config.liveHereBackgroundVideoSettings
      } else if (isLivingOptionRoute(this.$route.name)) {
        if (!this.activeLivingOption) return null
        return this.activeLivingOption.backgroundVideoSettings
      } else {
        return null
      }
    },

    controlsBottomPx() {
      return `${this.controlsBottom}px`
    },

    hasSubBottomBar() {
    return (this.$store.getters.activeSubBottomBar || []).length > 0
    },

    hasReferenceSlider() {
      const tags = this.$store.getters.activePageTags || []
      return tags.some(tag => tag && tag.type === 'reference')
    },

    controlsRightPx() {
      return `${this.controlsRight}px`
    },

    isReferenceSliderVertical() {
      // match ReferenceTagSlider.vue logic
      return this.$store.getters.activePage ? this.$store.getters.activePage.verticalSlider : true
    },

    isIframePage() {
      const page = this.$store.getters.activePage
      return !!(page && page.type === 'iframe')
    },

    iframeStyle() {
      if (this.sourceType !== 'iframe') return {}
      return {
        height: `calc(100% - ${this.iframeBottomOffset}px)`,
      }
    },
  },

  watch: {
    $route () {
      this.$store.commit('SET_ACTIVE_BACKGROUND_OVERRIDE', '')
      this.recalcSoon()
      this.recalcIframeSoon()
    },

    source: {
      handler(newSource, oldSource) {
        this.isVideoMuted = this.backgroundVideoSettings && this.backgroundVideoSettings.startMuted

        if (!_.isEqual(oldSource, newSource)) {
          if (oldSource !== '') {
            this.activeSource = ''
          }

          if (newSource !== '') {
            if (this.isIframePage && newSource) {
              this.startIframeLoading()
            }

            this.$store.commit('addAnimationToQueue', 'Background')

            if (this.backgroundVideoSettings && this.backgroundVideoSettings.delayTags) {
              this.$store.commit('addAnimationToQueue', 'BackgroundVideo')
            }
          }
        }
      },
      immediate: true
    },

    hasSubBottomBar() {
      this.recalcSoon()
      if (this.isIframePage) this.recalcIframeSoon()
    },

    hasReferenceSlider() {
      this.recalcSoon()
    },

    isReferenceSliderVertical() {
      this.recalcSoon()
    },

  },

  mounted () {
    bus.$on('animateBackground', () => {
      // this.transitionName = 'fade-in'
      this.isTransitionVideo = false
      this.activeSource = this.source
      bus.$emit('finishedAnimation', 'Background')
    })

    bus.$on('animateBackgroundTransition', () => {
      this.isTransitionVideo = true
      this.activeSource = this.$store.state.assetsPath + this.$store.state.activeTransitionBackground
      
      const animationDuration = 0
      setTimeout(() => {
        this.$refs.video.play()
      }, animationDuration)
    })

    bus.$on('finishedAnimation', (name) => {
      if (name === 'ReferenceTagSlider' || name === 'SubBottomBar') {
        this.recalcSoon()
        this.recalcIframeSoon()
      }
    })

    this.recalcSoon()
    this.recalcIframeSoon()
    window.addEventListener('resize', this.recalcSoon)
    window.addEventListener('resize', this.recalcIframeSoon)
  },
    
  beforeDestroy() {
    window.removeEventListener('resize', this.recalcSoon)
    if (this.controlsRafId) cancelAnimationFrame(this.controlsRafId)

    window.removeEventListener('resize', this.recalcIframeSoon)
    if (this.iframeRafId) cancelAnimationFrame(this.iframeRafId)

    if (this.iframeLoadTimeout) clearTimeout(this.iframeLoadTimeout)
  },

  methods: {
    handleVideoEnd() {
      if (this.isTransitionVideo) {
        bus.$emit('finishedAnimation', 'BackgroundTransition')
      } else {
        bus.$emit('finishedAnimation', 'BackgroundVideo')
      }
    },

    togglePlayback () {
      const video = this.$refs.video

      if (video && video.paused) {
        this.isVideoPlaying = true
        video.play()
      } else { 
        this.isVideoPlaying = false
        video.pause()
      }
    },

    toggleMute () {
      this.isVideoMuted = !this.isVideoMuted
    },

    remToPx(rem) {
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      return rem * rootFontSize
    },

    recalcSoon () {
      this.$nextTick(this.scheduleControlsRecalc)
    },


    scheduleControlsRecalc() {
      if (this.controlsRafId) cancelAnimationFrame(this.controlsRafId)

      this.controlsRafId = requestAnimationFrame(() => {
        this.recalcControlsBottom()
        this.controlsRafId = null
      })
    },

    recalcControlsBottom() {
      const baseBottomPx = this.remToPx(1)
      const gapPx = this.remToPx(-32)

      let extraBottomPx = 0
      let extraRightPx = 0

      // bottom shift only for sub-bottom bar (both orientations)
      if (this.hasSubBottomBar) {
        const el = document.querySelector('.sub-bottom-bar')
        if (el) extraBottomPx += el.getBoundingClientRect().height
      }

      // reference slider logic
      if (this.hasReferenceSlider) {
        const slider = document.querySelector('.reference-slider')
        if (slider) {
          const rect = slider.getBoundingClientRect()

          if (this.isReferenceSliderVertical) {
            // move controls LEFT so they don't overlap the vertical slider
            // place controls' right edge just left of slider's left edge
            extraRightPx = Math.max(0, window.innerWidth - rect.left + gapPx)
          } else {
            // horizontal slider occupies bottom space -> push controls UP
            extraBottomPx += rect.height
          }
        }
      }

      // reference video overlay 
      const referenceVideo = document.querySelector('.reference-video')
      if (referenceVideo && referenceVideo.getBoundingClientRect().height > 0) {
        extraBottomPx = Math.max(extraBottomPx, this.remToPx(10))
      }

      this.controlsBottom = Math.ceil(baseBottomPx + extraBottomPx)
      this.controlsRight = Math.ceil(extraRightPx) // 0 when no vertical slider
    },

    recalcIframeSoon() {
      this.$nextTick(this.scheduleIframeRecalc)
    },

    scheduleIframeRecalc() {
      if (this.iframeRafId) cancelAnimationFrame(this.iframeRafId)

      this.iframeRafId = requestAnimationFrame(() => {
        this.recalcIframeBottomOffset()
        this.iframeRafId = null
      })
    },

    recalcIframeBottomOffset() {
      // for iframe pages
      if (!this.isIframePage) {
        this.iframeBottomOffset = 0
        return
      }

      let offset = 0

      // account for sub-bottom bar if present
      const sub = document.querySelector('.sub-bottom-bar')
      if (sub && sub.getBoundingClientRect().height > 0) {
        offset += sub.getBoundingClientRect().height
      }

      this.iframeBottomOffset = Math.ceil(offset)
    },

    startIframeLoading() {
      this.isIframeLoading = true

      // fail-safe: hide loader if iframe never fires load (network / blocked / etc.)
      if (this.iframeLoadTimeout) clearTimeout(this.iframeLoadTimeout)
      this.iframeLoadTimeout = setTimeout(() => {
        this.isIframeLoading = false
      }, 15000) // 15s, 
    },

    handleIframeLoad() {
      this.isIframeLoading = false
      if (this.iframeLoadTimeout) {
        clearTimeout(this.iframeLoadTimeout)
        this.iframeLoadTimeout = null
      }
    },

    handleIframeError() {
      this.isIframeLoading = false
      if (this.iframeLoadTimeout) {
        clearTimeout(this.iframeLoadTimeout)
        this.iframeLoadTimeout = null
      }
    },


  }
}
</script>

<style lang="scss" scoped>
.background {
  height: 98rem;
  left: 0;
  position: absolute;
  top: 0;
  width: 159.5rem;
  z-index: var(--background-z-index);
  //background-color: red;

  .layout-switched & {
    top: 10rem;
  }

  &__wrapper {
    background-color: transparent;
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
  }

  &__control {
    bottom: 0;
    display: flex;
    font-size: 2rem;
    padding: 0 1rem;
    position: absolute;
    right: 0;
    // transition: bottom 0.25s ease, right 0.25s ease;

    .control {
      margin-left: 1rem;
    }
  }

  img,
  video {
    background-color: transparent;
    height: 100%;
    object-fit: cover;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
}

iframe.background__iframe {
  background-color: transparent;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.iframe-loader {
  position: absolute;
  inset: 0;
  z-index: 20001; // above background, below modals if needed
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);

  pointer-events: auto;

  &__spinner {
    width: 5rem;
    height: 5rem;
    border-radius: 50%;
    border: 0.5rem solid rgba(255, 255, 255, 0.25);
    border-top-color: rgba(255, 255, 255, 0.95);
    animation: spin 0.9s linear infinite;
  }

  &__text {
    margin-top: 1.5rem;
    font-size: 1.6rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    opacity: 0.9;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
